import { Box, Typography, Card, List, ListItem, ListItemText, IconButton, Chip } from '@mui/material';
import { Delete as DeleteIcon, MarkEmailRead as MarkReadIcon, MarkEmailUnread as MarkUnreadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCollection } from '../hooks/useFirestore';
import { db } from '../firebase';
import { deleteDoc, doc, query as fbQuery, orderBy as fbOrderBy, updateDoc } from 'firebase/firestore';

interface Feedback {
  id: string;
  userId?: string;
  userEmail?: string;
  content: string;
  screenshotUrl?: string | null;
  createdAt: Date;
  isRead?: boolean;
  readAt?: Date;
}

export default function FeedbacksPage() {
  const navigate = useNavigate();
  const { data: feedbacks } = useCollection<Feedback>(
    'feedback',
    (q) => fbQuery(q, fbOrderBy('createdAt', 'desc'))
  );

  const handleDelete = async (feedbackId: string) => {
    if (!window.confirm('このフィードバックを削除してもよろしいですか？')) return;
    try {
      await deleteDoc(doc(db, 'feedback', feedbackId));
    } catch (error) {
      console.error('Failed to delete feedback:', error);
    }
  };

  const handleMarkAsRead = async (feedbackId: string) => {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), {
        isRead: true,
        readAt: new Date()
      });
    } catch (error) {
      console.error('Failed to mark feedback as read:', error);
    }
  };

  const handleMarkAsUnread = async (feedbackId: string) => {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), {
        isRead: false,
        readAt: null
      });
    } catch (error) {
      console.error('Failed to mark feedback as unread:', error);
    }
  };

  const unreadCount = feedbacks?.filter(f => !f.isRead).length || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">フィードバック</Typography>
        {unreadCount > 0 && (
          <Chip 
            label={`未読 ${unreadCount}件`} 
            color="error" 
            size="small"
          />
        )}
      </Box>

      <Card>
        <List>
          {feedbacks?.map((f) => (
            <ListItem
              key={f.id}
              button
              onClick={() => navigate(`/feedbacks/${f.id}`)}
              sx={{
                backgroundColor: f.isRead ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                borderLeft: f.isRead ? 'none' : '4px solid #1976d2',
                '&:hover': {
                  backgroundColor: f.isRead ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.12)',
                }
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!f.isRead ? (
                    <IconButton 
                      edge="end" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(f.id);
                      }}
                      title="既読にする"
                    >
                      <MarkReadIcon />
                    </IconButton>
                  ) : (
                    <IconButton 
                      edge="end" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsUnread(f.id);
                      }}
                      title="未読に戻す"
                    >
                      <MarkUnreadIcon />
                    </IconButton>
                  )}
                  <IconButton 
                    edge="end" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(f.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: f.isRead ? 'normal' : 'bold',
                        color: f.isRead ? 'text.primary' : 'primary.main'
                      }}
                    >
                      {f.content}
                    </Typography>
                    {!f.isRead && (
                      <Chip 
                        label="未読" 
                        size="small" 
                        color="error" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={`${f.userEmail || f.userId || '匿名'} / ${(() => { const v: any = (f as any).createdAt; try { const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v); return d.toLocaleString(); } catch { return ''; } })()}`}
              />
            </ListItem>
          ))}
        </List>
      </Card>
    </Box>
  );
}


